import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'reactstrap';
import QuestionType from './QuestionType';
import FormGroup from 'reactstrap/lib/FormGroup';
import Label from 'reactstrap/lib/Label';
import Input from 'reactstrap/lib/Input';
import { withTranslation } from 'react-i18next';

class QuestionMultipleChoice extends QuestionType {
  constructor(props) {
    super(props);
    this.state = {
      update: this.state.update,
      options: null,
      maxSelectionError: null,
    };
  }

  componentDidMount() {
    this.loadOptions();
    // Ensure hierarchical parents are added for any pre-selected values
    if (this.isHierarchical()) {
      setTimeout(() => this.ensureHierarchicalParentsAdded(), 200);
    }
  }

  updateFields() {
    this.loadOptions();
    // Re-check hierarchical parents when fields are updated
    if (this.isHierarchical()) {
      setTimeout(() => this.ensureHierarchicalParentsAdded(), 200);
    }
  }

  findOptionsInOptionPath(option_id, parentOptions = null) {
    if (!parentOptions) {
      parentOptions = this.state.options;
    }
    for (const p of parentOptions) {
      if (p.id === option_id) {
        return [p];
      }
      if (p.children) {
        const parent_e = this.findOptionsInOptionPath(option_id, p.children);
        if (parent_e.length > 0) {
          return [...parent_e, p];
        }
      }
    }
    return [];
  }

  /**
   * Finds parent options for a given option_id using ALL options (not just filtered/state options)
   * This is important for hierarchical fields where parent options might be filtered out
   * but child options are still selected via vorauswahl or user selection
   */
  findParentOptionsFromAllOptions(option_id) {
    const allOptions = this.question().options || [];
    const parentFieldName = this.getOptionParentFieldName();
    
    // Find the option in all options
    const findOption = (id) => allOptions.find(o => 
      this.easyCompare(o.id, id) || this.easyCompare(o._id, id)
    );
    
    const option = findOption(option_id);
    if (!option) return [];
    
    const parents = [];
    let currentOption = option;
    
    // Traverse up the hierarchy to find all parents
    while (currentOption && currentOption[parentFieldName]) {
      const parentRef = currentOption[parentFieldName];
      const parentId = parentRef._id || parentRef.id || parentRef;
      const parentOption = findOption(parentId);
      
      if (parentOption) {
        parents.push(parentOption);
        currentOption = parentOption;
      } else {
        break;
      }
    }
    
    return parents;
  }

  /**
   * Ensures all hierarchical parent elements are added for all selected values
   * This is called after vorauswahl processing or when values change
   */
  ensureHierarchicalParentsAdded() {
    if (!this.isHierarchical()) return;
    
    const currentValue = this.getValue();
    if (!currentValue || !Array.isArray(currentValue)) return;
    
    // Get all currently selected IDs
    const selectedIds = currentValue
      .filter(v => {
        if (typeof v === 'string') return true;
        return v && (v.checked === true || typeof v.checked === 'undefined');
      })
      .map(v => typeof v === 'string' ? v : v.id)
      .filter(id => id);
    
    // Find all parents that should be added
    const parentsToAdd = new Set();
    
    for (const selectedId of selectedIds) {
      const parents = this.findParentOptionsFromAllOptions(selectedId);
      for (const parent of parents) {
        const parentId = (parent.id || parent._id || '').toLowerCase();
        if (parentId && !selectedIds.some(id => this.easyCompare(id, parentId))) {
          parentsToAdd.add(parentId);
        }
      }
    }
    
    // Add all missing parent elements
    for (const parentId of parentsToAdd) {
      // Check if not already added
      if (!selectedIds.some(id => this.easyCompare(id, parentId))) {
        super.addValue(parentId);
      }
    }
  }

  findOptionById(option_id, options = null) {
    if (!options) {
      options = this.state.options;
    }
    for (const p of options) {
      if (this.easyCompare(p.id, option_id)) {
        return p;
      }
      if (p.children) {
        const parent_p = this.findOptionById(option_id, p.children);
        if (parent_p) {
          return parent_p;
        }
      }
    }
    return null;
  }

  hasChildOptionsSelected(option_id, options = null) {
    if (!options) {
      const first_option = this.findOptionById(option_id);
      if (typeof first_option === 'object' && first_option.children) {
        options = first_option.children;
      } else {
        options = [];
      }
    }
    for (const p of options) {
      if (this.hasValue(p.id) && p.id != option_id) {
        return true;
      }
      if (p.children) {
        if (this.hasChildOptionsSelected(p.id, p.children)) {
          return true;
        }
      }
    }

    return false;
  }

  removeValue(value) {
    // When there are options in childs clicked - deselection is not possible
    if (this.hasChildOptionsSelected(value)) {
      return false;
    }

    return super.removeValue(value);
  }

  addValue(value) {
    // First try to find parents in the current (filtered) hierarchical options
    const parentQuestions = this.findOptionsInOptionPath(value);

    for (const p of parentQuestions) {
      super.addValue(p.id.toLowerCase());
    }

    // If hierarchical, also check ALL options for parents (in case they were filtered out)
    if (this.isHierarchical() && parentQuestions.length === 0) {
      const parentsFromAllOptions = this.findParentOptionsFromAllOptions(value);
      for (const p of parentsFromAllOptions) {
        const parentId = (p.id || p._id || '').toLowerCase();
        if (parentId) {
          super.addValue(parentId);
        }
      }
    }

    return super.addValue(value);
  }

  // Add method to get the maximum allowed selections
  getMaxElements() {
    // Check if question has a maxSelections property, otherwise default to unlimited
    return this.question().maxElements || null;
  }

  // Add method to count current selections
  getCurrentSelectionCount() {
    const val = this.qCtrl().getValueForQuestion(this.question());
    if (val && Array.isArray(val)) {
      return val.filter(
        (f) =>
          typeof f === 'string' ||
          (f && typeof f.checked !== "undefined" && f.checked == true) ||
          (f && typeof f.checked === 'undefined' && typeof f.id !== 'undefined')
      ).length;
    }
    return val ? 1 : 0;
  }

  onOptionChanged(e, option) {
    const maxSelections = this.getMaxElements();
    const currentCount = this.getCurrentSelectionCount();

    // If trying to check a box and we've reached the limit
    if (e.target.checked && maxSelections && currentCount >= maxSelections) {
      // Prevent checking and show error message
      e.preventDefault();
      this.setState({
        maxSelectionError: `Sie können maximal ${maxSelections} Optionen auswählen.`
      });
      return false;
    }

    // Clear any previous max selection error
    if (this.state.maxSelectionError) {
      this.setState({ maxSelectionError: null });
    }

    // If the value before was "null"
    // then we now - add all the vorauswahl elements here
    if (this.getValue() == null) {
      const vorauswahl = this.qCtrl().getVorauswahlforQuestion(this.question());
      if (vorauswahl) {
        if (Array.isArray(vorauswahl)) {
          for (const v of vorauswahl) {
            if (typeof v === 'string') {
              this.addValue(v.toLowerCase());
            }
          }
        }
      }
      // Ensure all hierarchical parent elements are added after vorauswahl processing
      if (this.isHierarchical()) {
        setTimeout(() => this.ensureHierarchicalParentsAdded(), 100);
      }
    }

    if (e.target.checked) {
      this.addValue(option.id.toLowerCase());
      // Ensure hierarchical parents are added after user selection
      if (this.isHierarchical()) {
        setTimeout(() => this.ensureHierarchicalParentsAdded(), 100);
      }
    } else {
      this.removeValue(option.id.toLowerCase());
    }
  }

  renderBadge(q) {
    try {
      if (
        typeof q.Badge !== 'undefined' &&
        q.Badge != null &&
        q.Badge.length > 1
      ) {
        return (
          <div className={'question-badge'}>
            <span>{q.Badge}</span>
          </div>
        );
      }
    } catch (e) {}
    return null;
  }

  easyCompare(v1, v2, only_left_to_right = false) {
    if (v1 && v2) {
      if (Array.isArray(v1)) {
        v1 = v1.join(',');
      }
      if (Array.isArray(v2)) {
        v2 = v2.join(',');
      }
      v1 = v1
        .toString()
        .replace(/\./g, '')
        .replace(/\_/g, '')
        .replace(/\-/g, '')
        .toLowerCase();
      v2 = v2
        .toString()
        .replace(/\./g, '')
        .replace(/\_/g, '')
        .replace(/\-/g, '')
        .toLowerCase();
      if (v1.length > 0 && v2.length > 0) {
        if (v1.includes(v2) || (!only_left_to_right && v2.includes(v1))) {
          return true;
        }
      }
    }
    return false;
  }

  // Add method to render max selection error
  renderMaxSelectionError() {
    if (this.state.maxSelectionError) {
      return (
        <div className="selection-error-modal">
          <div className="selection-error-modal-overlay" onClick={() => this.setState({ maxSelectionError: null })}>
            <div className="selection-error-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Auswahlgrenze erreicht
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  aria-label="Close"
                  onClick={() => this.setState({ maxSelectionError: null })}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">{this.state.maxSelectionError}</p>
                <p className="text-muted small mb-0">
                  Bitte deaktivieren Sie eine andere Option, um eine neue auswählen zu können.
                </p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => this.setState({ maxSelectionError: null })}
                >
                  Verstanden
                </button>
              </div>
            </div>
          </div>
          <style jsx>{`
            .selection-error-modal {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              z-index: 1050;
            }
            .selection-error-modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.5);
              backdrop-filter: blur(2px);
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .selection-error-modal-content {
              position: relative;
              background: white;
              border-radius: 8px;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
              max-width: 500px;
              width: 90%;
              max-height: 90vh;
              overflow: auto;
              animation: modalFadeIn 0.3s ease-out;
            }
            .modal-header {
              padding: 1rem 1.5rem;
              border-bottom: 1px solid #dee2e6;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .modal-body {
              padding: 1.5rem;
            }
            .modal-footer {
              padding: 1rem 1.5rem;
              border-top: 1px solid #dee2e6;
              display: flex;
              justify-content: flex-end;
            }
            @keyframes modalFadeIn {
              from {
                opacity: 0;
                transform: scale(0.8) translateY(-20px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
          `}</style>
        </div>
      );
    }
    return null;
  }

  // Add method to render selection counter
  renderSelectionCounter() {
    const maxSelections = this.getMaxElements();
    if (maxSelections) {
      const currentCount = this.getCurrentSelectionCount();
      const percentage = (currentCount / maxSelections) * 100;
      const isNearLimit = currentCount >= maxSelections * 0.8;
      const isAtLimit = currentCount >= maxSelections;
      
      return (
        <div className={`selection-counter-prominent mb-3 p-3 border rounded`} style={{borderColor: isAtLimit ? '#dc3545' : '#17a2b8'}}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong style={{ color: isAtLimit ? '#dc3545' : '#17a2b8' }}>
                Auswahl: {currentCount > maxSelections ? maxSelections : currentCount} von {maxSelections} Optionen
              </strong>
              {isAtLimit && <div className="small text-danger mt-1">Maximum erreicht</div>}
            </div>
            <div className={`badge fs-6`} style={{backgroundColor: isAtLimit ? '#dc3545' : '#17a2b8'}}>
              {percentage > 100 ? 100 : Math.round(percentage)}%
            </div>
          </div>
          <div className="progress mt-2" style={{ height: '6px' }}>
            <div 
              className={`progress-bar`}
              role="progressbar" 
              style={{ width: `${percentage}%`, backgroundColor: isAtLimit ? '#dc3545' : '#17a2b8' }}
              aria-valuenow={currentCount}
              aria-valuemin="0" 
              aria-valuemax={maxSelections}
            ></div>
          </div>
        </div>
      );
    }
    return null;
  }

  hasValue(valset) {
    let val = this.qCtrl().getValueForQuestion(this.question());
    if (val != null) {
      if (!Array.isArray(val)) {
        val = [val];
      }
      if (
        this.easyCompare(
          _.cloneDeep(val)
            .filter(
              (f) =>
                typeof f === 'string' ||
                (f && typeof f.checked !== "undefined" && f.checked == true) ||
                (f && typeof f.checked === 'undefined' &&
                  typeof f.id !== 'undefined')
            )
            .map((r) =>
              typeof r === 'string'
                ? r
                : typeof r.id === 'string'
                ? r.id.toLowerCase()
                : r.id
            ),
          valset,
          true
        )
      ) {
        return true;
      }
    } else {
      const vorauswahl = this.qCtrl().getVorauswahlforQuestion(this.question());
      if (vorauswahl != null) {
        if (Array.isArray(vorauswahl)) {
          if (
            this.easyCompare(
              _.cloneDeep(vorauswahl).map((r) =>
                typeof r === 'string' ? r.toLowerCase() : r
              ),
              valset,
              true
            )
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  renderOptions(options = null, level = 0) {
    const list = [];
    let opts = options ? options : this.state.options;
    if (opts) {
      opts = this.filterOptionsBySearch(opts);

      let columnWidth = 12;
      if (this.question().gridableElements === true) {
        columnWidth = 4;
      }

      const t = this.props.t;

      for (const o of opts) {
        const id = o.id ? o.id.toLowerCase() : o._id ? o._id.toLowerCase() : o;
        const checked = this.hasValue(id);
        const disabled = this.isFinished();
        list.push(
          <Col md={columnWidth} className={'p-1 option'}>
            <Label
              className={
                (checked ? 'is-selected' : '') + (disabled ? ' is-disabled' : '')
              }
            >
              <Input
                disabled={disabled}
                type={'checkbox'}
                checked={checked}
                onChange={(e) => this.onOptionChanged(e, o)}
                name={this.questionName()}
              />
              {o.text ? t(o.text) : t(o.label)} {this.renderBadge(o)}
            </Label>
            <div className={'child-options level-' + level}>
              {o.children && this.renderOptions(o.children, level + 1)}
            </div>
          </Col>
        );
      }
    }

    return <Row className={'option-containers'}>{list}</Row>;
  }

  hasReset() {
    return true;
  }

  /**
   * Called when user clicks the reset button.
   * After reset completes, triggers update for dependent questions (including QuestionRepeated).
   * Note: This is called BEFORE qCtrl().resetQuestion(), so we use a longer timeout
   * to ensure the reset and preselection have completed.
   */
  onResetQuestion() {
    // Set isVorauswahl to true so preselection is applied
    const questionId = this.question().id;
    const answer = this.qCtrl().formular.state.answers[questionId];
    if (answer) {
      answer.isVorauswahl = true;
    }

    // Wait for the actual reset and preselection to complete (resetQuestion + onAnswersUpdated)
    // then trigger a "fake" setValue to fire postChangeMessage for dependent questions
    setTimeout(() => {
      const currentValue = this.getValue();
      if (currentValue) {
        // Use postChangeMessage directly (inherited from QuestionType) 
        // to notify QuestionRepeated and other listeners
        this.postChangeMessage(currentValue, this.question());
      }
    }, 800);
  }

  render() {
    return (
      <>
        <Container fluid={true}>
          {this.isSearchable() && this.renderSearchbar()}
          {this.renderSelectionCounter()}
          <Row>
            <Col className={'p-1'} md={12}>
              <FormGroup
                className={
                  'p-1 multiple-choice-container ' +
                  (this.hasError() ? 'error' : '')
                }
              >
                {this.renderOptions()}
                {this.renderErrors()}
              </FormGroup>
            </Col>
          </Row>
        </Container>
        {this.renderMaxSelectionError()}
      </>
    );
  }
}

QuestionMultipleChoice.propTypes = {};

export default withTranslation()(QuestionMultipleChoice);
